import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input, notification } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { groupForCategory } from '@totem/components/services/threats/eventsDashboard/report/configuration';
import EventsReportContext from '@totem/components/services/threats/eventsDashboard/report/EventsReportContext';
import {
  AgePill,
  SeverityPill,
  StatusPill,
} from '@totem/components/services/threats/eventsDashboard/report/pills';
import {
  EMPTY_ID,
  FINDING_SEVERITIES,
  formatDuration,
} from '@totem/components/services/threats/eventsDashboard/report/severity';
import {
  DashboardFindings,
  DashboardTotals,
  FindingGroup,
  FindingRow,
} from '@totem/components/services/threats/eventsDashboard/report/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { debounce } from '@totem/utilities/debounce';
import { V2_EVENTS_ENDPOINT } from '@totem/utilities/endpoints';

import './eventsReport.css';

const DEBOUNCE_TIME = 300;

const EMPTY_TOTALS: DashboardTotals = {
  events: 0,
  findings: 0,
  critical: 0,
  major: 0,
  minor: 0,
  withTicket: 0,
  operations: 0,
  security: 0,
  buildings: 0,
  longestOpenDays: 0,
  averageResolveHours: 0,
};

const GROUP_TEXT: Record<
  FindingGroup,
  { title: string; resolvedTitle: string; meta: string; note: string }
> = {
  operations: {
    title: 'What IB is working on',
    resolvedTitle: 'What IB has resolved',
    meta: 'Service and equipment issues across your buildings · most urgent first',
    note: 'Service and equipment issues. Select a row to see what it means, why it matters and what IB is doing.',
  },
  security: {
    title: 'Security',
    resolvedTitle: 'Resolved security issues',
    meta: 'Endpoint protection and network threat detection across your buildings · most urgent first',
    note: 'Security issues. Select a row for the full picture: what it means, why it matters and what IB is doing.',
  },
};

const Stat = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  color?: string;
}) => (
  <div styleName="hero-stat">
    <div styleName="kpi-label">{label}</div>
    <div styleName="kpi-value" style={color ? { color } : undefined}>
      {value}
    </div>
    <div styleName="kpi-caption">{sub}</div>
  </div>
);

type Props = {
  group: FindingGroup;
};

const FindingsTab = ({ group }: Props) => {
  const navigate = useNavigate();
  const { getApiToken } = useContext(UserProfileContext);
  const {
    summary,
    mode,
    reportKey,
    buildReportParameters,
    category,
    clearCategory,
  } = useContext(EventsReportContext);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [severities, setSeverities] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [findings, setFindings] = useState<DashboardFindings>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const isResolved = mode === 'resolved';

  const sendSearch = useCallback(
    debounce((term: string) => {
      setPage(1);

      setSearch(term);
    }, DEBOUNCE_TIME),
    [],
  );

  // A service area picked on the Summary tab narrows the tab it belongs to.
  const activeCategory =
    category !== null && groupForCategory(category) === group ? category : null;
  const categoryName =
    summary?.categories.find((item) => item.key === activeCategory)?.name ??
    activeCategory;

  useEffect(() => {
    setPage(1);
  }, [reportKey, activeCategory]);

  useEffect(() => {
    const params = buildReportParameters();
    const filters = [
      ...params.filters,
      { field: 'group', values: [group] },
      ...(activeCategory !== null
        ? [{ field: 'category', values: [activeCategory] }]
        : []),
      ...(severities.length > 0
        ? [{ field: 'findingSeverity', values: severities }]
        : []),
      ...(search !== '' ? [{ field: 'search', values: [search] }] : []),
    ];

    setLoading(true);

    fetch(`${V2_EVENTS_ENDPOINT}/dashboard/findings`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({ ...params, paging: { page, pageSize }, filters }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`${res.status}`);
        }
        return res.json();
      })
      .then((result: DashboardFindings) => setFindings(result))
      .catch(() =>
        notification.error({
          title: 'Events',
          description: 'The issue list could not be loaded.',
        }),
      )
      .finally(() => setLoading(false));
  }, [reportKey, group, activeCategory, page, pageSize, severities, search]);

  if (!summary) {
    return null;
  }

  const text = GROUP_TEXT[group];
  const totals: DashboardTotals = summary.groupTotals[group] ?? EMPTY_TOTALS;
  const groupTotal = totals.findings;

  const columns: ColumnProps<FindingRow>[] = [
    {
      title: 'Severity',
      key: 'severity',
      width: 110,
      render: (_, finding) => <SeverityPill severity={finding.severity} />,
    },
    {
      title: 'Where',
      key: 'where',
      width: 180,
      render: (_, finding) => (
        <>
          <div styleName="issue-title">
            {finding.building.id !== EMPTY_ID
              ? finding.building.name
              : 'Unassigned'}
          </div>
          <div styleName="sub">{finding.device.name}</div>
        </>
      ),
    },
    {
      title: 'Issue',
      key: 'issue',
      render: (_, finding) => (
        <>
          <Link
            styleName="issue-title"
            to={`/dashboard/events2/${finding.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {finding.eventType}
          </Link>
          <div styleName="sub">{finding.description}</div>
        </>
      ),
    },
    {
      title: isResolved ? 'Resolved in' : 'Open for',
      key: 'age',
      width: 100,
      render: (_, finding) => <AgePill days={finding.ageDays} />,
    },
    {
      title: 'Ticket',
      key: 'ticket',
      width: 110,
      render: (_, finding) =>
        finding.ticket ? (
          <Link
            to={`/dashboard/ticket/${finding.ticket.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {finding.ticket.number}
          </Link>
        ) : (
          <span styleName="sub">—</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, finding) => (
        <StatusPill
          status={finding.status}
          hasTicket={finding.ticket !== null}
        />
      ),
    },
  ];

  return (
    <div>
      <div styleName="hero">
        <div styleName="hero-head">
          <div>
            <div styleName="hero-title">
              {isResolved ? text.resolvedTitle : text.title}
            </div>
            <div styleName="sub">{text.meta}</div>
          </div>
          <div styleName="sub">
            {groupTotal} issue{groupTotal === 1 ? '' : 's'}
          </div>
        </div>
        <div styleName="hero-stats">
          {isResolved ? (
            <>
              <Stat
                label="Resolved"
                value={totals.findings}
                sub="Minor and above"
                color="#22c55e"
              />
              <Stat
                label="Average time to resolve"
                value={
                  totals.findings > 0
                    ? formatDuration(totals.averageResolveHours)
                    : '—'
                }
                sub="opened to resolved"
              />
              <Stat
                label="Buildings"
                value={totals.buildings}
                sub="with a resolved issue"
              />
              <Stat
                label="With a ticket"
                value={`${totals.withTicket} of ${totals.findings}`}
                sub="tracked by IB support"
              />
            </>
          ) : (
            <>
              <Stat
                label="Critical · open"
                value={totals.critical}
                sub={`${totals.major} major`}
                color={totals.critical > 0 ? '#ef4444' : '#22c55e'}
              />
              <Stat
                label="Longest open"
                value={
                  totals.findings > 0
                    ? `${Math.round(totals.longestOpenDays)} days`
                    : '—'
                }
                sub="oldest open issue"
              />
              <Stat
                label="Open issues"
                value={totals.findings}
                sub={`of ${(summary?.processed.events ?? 0).toLocaleString()} events processed`}
                color={totals.findings > 0 ? '#f59e0b' : undefined}
              />
              <Stat
                label="With a ticket"
                value={`${totals.withTicket} of ${totals.findings}`}
                sub="tracked by IB support"
              />
            </>
          )}
        </div>
      </div>

      <div styleName="filter-bar">
        <span styleName="filter-label">FILTER</span>
        {activeCategory !== null && (
          <button
            type="button"
            styleName="chip chip-on"
            title="Show every service area"
            onClick={clearCategory}
          >
            {categoryName} ✕
          </button>
        )}
        <button
          type="button"
          styleName={severities.length === 0 ? 'chip chip-on' : 'chip'}
          onClick={() => {
            setPage(1);

            setSeverities([]);
          }}
        >
          All severities
        </button>
        {FINDING_SEVERITIES.map((severity) => {
          const value = String(severity.value);
          const isOn = severities.length === 1 && severities[0] === value;
          return (
            <button
              key={severity.key}
              type="button"
              styleName={isOn ? 'chip chip-on' : 'chip'}
              onClick={() => {
                setPage(1);

                setSeverities(isOn ? [] : [value]);
              }}
            >
              {severity.label}
            </button>
          );
        })}
        <Input
          allowClear
          styleName="search"
          placeholder="Search building, device, issue…"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);

            sendSearch(event.target.value);
          }}
        />
      </div>
      <div styleName="note">{text.note}</div>

      <IBTable
        size="small"
        columns={columns}
        dataSource={findings?.findings ?? []}
        loading={loading}
        rowKey={(finding) => finding.id}
        scroll={{ x: 'max-content' }}
        onRow={(finding) => ({
          onClick: () => navigate(`/dashboard/events2/${finding.id}`),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText:
            search !== '' || severities.length > 0 || activeCategory !== null
              ? 'No issues match the current filters. Clear a filter above to see more.'
              : isResolved
                ? 'Nothing resolved in this period.'
                : 'No open issues here. Nothing needs your attention right now.',
        }}
        pagination={{
          current: page,
          pageSize,
          total: findings?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} issue${total === 1 ? '' : 's'}`,
          onChange: (newPage, newPageSize) => {
            setPage(newPageSize !== pageSize ? 1 : newPage);

            setPageSize(newPageSize);
          },
        }}
      />

      {group === 'operations' &&
        !isResolved &&
        summary.alsoNoted.length > 0 && (
          <details styleName="noted">
            <summary>
              Also noted (
              {summary.alsoNoted
                .reduce((sum, item) => sum + item.count, 0)
                .toLocaleString()}
              ) — routine activity and low-severity events, tracked by IB
            </summary>
            {summary.alsoNoted.map((item) => (
              <div
                key={`${item.category}-${item.eventType}`}
                styleName="noted-row"
              >
                <b>{item.eventType || 'Not recorded'}</b>
                <span styleName="num">{item.count.toLocaleString()}</span>
                <span styleName="sub">{item.meaning}</span>
              </div>
            ))}
          </details>
        )}
    </div>
  );
};

export default FindingsTab;
